import { useState, useEffect, useRef, useCallback } from "react";
import { apiCall } from "../../../../../../_services/apiCall";
import config from "../../../../../../config/config.json";

/* ── Stable hash ─────────────────────────────────────────────────────────── */
function hashData(data) {
    if (!data) return "";
    try {
        const str = JSON.stringify(data);
        let h = 0;
        for (let i = 0; i < str.length; i++) {
            h = Math.imul(31, h) + str.charCodeAt(i) | 0;
        }
        return String(h >>> 0);
    } catch { return ""; }
}

/* ── Module-level insights cache ─────────────────────────────────────────────
   Cache key = `${tabName}::${graphId}` — scoped per tab so graphs from
   different tabs never share cached insights even if they have the same ID.
─────────────────────────────────────────────────────────────────────────── */
const _cache = new Map(); // `tabName::graphId` → { hash, insights }

export const insightsCache = {
    key:   (tabName, graphId) => `${tabName}::${graphId}`,
    has:   (tabName, graphId, hash) => {
        const k = `${tabName}::${graphId}`;
        return _cache.has(k) && _cache.get(k).hash === hash;
    },
    get:   (tabName, graphId) => _cache.get(`${tabName}::${graphId}`),
    set:   (tabName, graphId, hash, ins) => _cache.set(`${tabName}::${graphId}`, { hash, insights: ins }),
    clear: (tabName, graphId) => _cache.delete(`${tabName}::${graphId}`),
    /** Clear ALL entries for a given tab — call on tab unmount / refresh */
    clearTab: (tabName) => {
        const prefix = `${tabName}::`;
        _cache.forEach((_, k) => { if (k.startsWith(prefix)) _cache.delete(k); });
    },
};

/* ── Global sequential queue ─────────────────────────────────────────────────
   Only ONE insights API call runs at a time across all tabs/graphs.
─────────────────────────────────────────────────────────────────────────── */
const _queue = [];
let _running = false;

function enqueue(fn) {
    return new Promise((resolve, reject) => {
        _queue.push(async () => {
            try { resolve(await fn()); }
            catch (e) { reject(e); }
        });
        drain();
    });
}

async function drain() {
    if (_running || _queue.length === 0) return;
    _running = true;
    while (_queue.length > 0) {
        const task = _queue.shift();
        await task();
    }
    _running = false;
}

/* ── useInsights ─────────────────────────────────────────────────────────────
   @param tabName    string  — current module tab (used as cache namespace)
   @param graphId    string  — stable graph id
   @param graphData  object  — { data[], chartType, title, … }
   @param meta       object  — graph metadata
   @param enabled    bool    — false = don't fetch
   @param onReady    fn      — called with { graphId, title, narrative|null } on resolve
                               graphId is included so the parent can deduplicate by ID,
                               not title — title may change on edit.
─────────────────────────────────────────────────────────────────────────── */
export function useInsights(tabName, graphId, graphData, meta, enabled = true, onReady) {
    const [insights, setInsights] = useState(null);
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState(null);

    const lastHashRef = useRef(null);
    const onReadyRef  = useRef(onReady);
    useEffect(() => { onReadyRef.current = onReady; }, [onReady]);

    const seededRef = useRef(false);

    const notify = useCallback((ins) => {
        // Always include graphId so the parent can key by ID, not title
        onReadyRef.current?.({
            graphId,
            title:     ins?._title     || meta?.title || "",
            narrative: (!ins || ins._empty) ? null : (ins.narrative || null),
        });
    }, [graphId, meta?.title]); // eslint-disable-line

    const doFetch = useCallback(async (force = false) => {
        if (!enabled || !graphData?.data) return;
        const currentHash = hashData(graphData.data);

        // Cache hit — serve immediately, no API call
        if (!force && insightsCache.has(tabName, graphId, currentHash)) {
            const cached = insightsCache.get(tabName, graphId).insights;
            setInsights(cached);
            lastHashRef.current = currentHash;
            notify(cached);
            return;
        }

        setLoading(true);
        setError(null);

        await enqueue(async () => {
            try {
                const { isSuccess, data } = await apiCall(
                    `${config.POSTLOGIN_API_URL_COMPANY}bi-graph/insights/preview`,
                    {},
                    {
                        graphData: graphData.data,
                        graphContext: {
                            title:              meta?.title,
                            chartType:          meta?.chartType,
                            representationType: meta?.representationType,
                            widgetConfig:       meta?.widgetConfig,
                        },
                    },
                    "POST", false, false
                );

                if (typeof data === "string") {
                    const empty = { _empty: true, message: data, _title: meta?.title || "" };
                    setInsights(empty);
                    insightsCache.set(tabName, graphId, currentHash, empty);
                    lastHashRef.current = currentHash;
                    notify(empty);
                } else if (isSuccess && data) {
                    const enriched = { ...data, _title: meta?.title || "" };
                    setInsights(enriched);
                    insightsCache.set(tabName, graphId, currentHash, enriched);
                    lastHashRef.current = currentHash;
                    notify(enriched);
                } else {
                    setError("Failed to load insights");
                    onReadyRef.current?.({ graphId, title: meta?.title || "", narrative: null });
                }
            } catch (err) {
                console.error("Insights fetch failed", err);
                setError("Failed to load insights");
                onReadyRef.current?.({ graphId, title: meta?.title || "", narrative: null });
            } finally {
                setLoading(false);
            }
        });
    }, [tabName, graphId, graphData, meta, enabled, notify]); // eslint-disable-line

    useEffect(() => {
        if (!enabled) {
            seededRef.current = false;
            return;
        }
        if (!graphData?.data) return;

        const currentHash = hashData(graphData.data);

        // Toggle back on: restore from cache silently, no API call
        if (!seededRef.current && insightsCache.has(tabName, graphId, currentHash)) {
            seededRef.current = true;
            const cached = insightsCache.get(tabName, graphId).insights;
            setInsights(cached);
            lastHashRef.current = currentHash;
            notify(cached);
            return;
        }

        if (currentHash !== lastHashRef.current || insights === null) {
            seededRef.current = true;
            doFetch(false);
        }
    }, [graphData, enabled, tabName]); // eslint-disable-line

    const refresh = useCallback(() => doFetch(true), [doFetch]);

    return { insights, loading, error, refresh };
}